import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from 'shared/hooks/useAuth';
import { useAsyncData } from 'shared/hooks/useAsyncData';
import { BookingSummary, Participant, PaymentMethod } from 'modules/jogador/checkout/types';
import { DEFAULT_BOOKING_SUMMARY, ORDER_ID, SERVICE_FEE } from 'modules/jogador/checkout/content';
import { fetchSplitParticipants, payOrder } from 'modules/jogador/checkout/checkoutService';

const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  pix: 'Pix',
  cartao: 'Cartão',
};

function formatDeadline(minutesFromNow: number): string {
  const deadline = new Date(Date.now() + minutesFromNow * 60_000);
  return deadline.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

export function useCheckout() {
  const { user } = useAuth();
  const location = useLocation();
  const booking = (location.state as BookingSummary | null) ?? DEFAULT_BOOKING_SUMMARY;

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('pix');
  const [hasPaid, setHasPaid] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [deadline] = useState(() => formatDeadline(15));

  const { data: splitParticipants, isLoading: isLoadingParticipants } = useAsyncData<Participant[]>(
    () => (booking.splitEnabled ? fetchSplitParticipants(ORDER_ID) : Promise.resolve([])),
    [],
    [booking.splitEnabled]
  );
  const otherParticipants = booking.splitEnabled
    ? splitParticipants.slice(0, Math.max(0, booking.maxPlayers - 1))
    : [];

  const you: Participant = {
    id: 'you',
    name: user?.name ?? 'Você',
    isYou: true,
    status: hasPaid ? 'paid' : 'paying',
    methodLabel: hasPaid
      ? `${PAYMENT_METHOD_LABELS[paymentMethod]} · pago`
      : `${PAYMENT_METHOD_LABELS[paymentMethod]} · pagando agora`,
  };

  const participants = [you, ...otherParticipants];
  const quotaCount = booking.splitEnabled ? booking.maxPlayers : 1;
  const totalAmount = booking.pricePerHour + SERVICE_FEE;
  const quotaAmount = totalAmount / quotaCount;
  const paidCount = participants.filter((participant) => participant.status === 'paid').length;

  async function pay() {
    setIsPaying(true);
    try {
      await payOrder(ORDER_ID, paymentMethod);
      setHasPaid(true);
    } finally {
      setIsPaying(false);
    }
  }

  return {
    orderId: ORDER_ID,
    booking,
    deadline,
    participants,
    isLoadingParticipants: booking.splitEnabled && isLoadingParticipants,
    quotaCount,
    paidCount,
    totalAmount,
    quotaAmount,
    courtPrice: booking.pricePerHour,
    serviceFee: SERVICE_FEE,
    paymentMethod,
    setPaymentMethod,
    hasPaid,
    isPaying,
    pay,
  };
}
